import { ReactNode, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import style from "./style.module.css";

interface MobileDesignActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

const SWIPE_DISMISS_THRESHOLD = 80;

const MobileDesignActionSheet = ({
  isOpen,
  onClose,
  children
}: MobileDesignActionSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const currentTranslateY = useRef<number>(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartY.current = touch.clientY;
    currentTranslateY.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!sheetRef.current || !touch) return;
    const deltaY = touch.clientY - touchStartY.current;
    const translate = Math.max(0, deltaY);
    currentTranslateY.current = translate;
    sheetRef.current.style.transform = `translateY(${translate}px)`;
    sheetRef.current.style.transition = "none";
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!sheetRef.current) return;
    sheetRef.current.style.transition = "";

    if (currentTranslateY.current > SWIPE_DISMISS_THRESHOLD) {
      onClose();
    } else {
      sheetRef.current.style.transform = "";
    }
    currentTranslateY.current = 0;
  }, [onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className={style.backdrop} data-open={isOpen} onClick={onClose} />
      <div
        ref={sheetRef}
        className={style.sheet}
        data-open={isOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Design actions"
      >
        <div
          className={style.handleArea}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={style.handleBar} />
        </div>
        <div className={style.sheetContent}>{children}</div>
      </div>
    </>,
    document.body
  );
};

export default MobileDesignActionSheet;
